// Transformações das respostas do BE (WSGI) para os tipos da tela de Visão
// Geral Agro. Contrato congelado 2026-08-07:
// espaco-exclusivo-wsgi-beta/docs/contracts/agro-overview.md (§2/§3).
// Regra de ouro: o BE repassa o payload da fonte intacto (exceto `uf` na
// tabela, DECISÃO-2); TODA transformação (snake_case→camelCase, tripletas,
// enum de risco) acontece aqui.
import {
  AgroOverviewLocation,
  AgroOverviewLocationsData,
  AgroOverviewMapData,
  AgroOverviewMapVariable,
  AgroOverviewSummary,
  AgroOverviewTableVariable,
  RiskLevel,
} from 'types/Meteorology/Agro/Overview/Dashboard';
import {
  GetAgroOverviewKpisResponse,
  GetAgroOverviewMapaResponse,
  GetAgroOverviewMapaVariaveisResponse,
  GetAgroOverviewTabelaResponse,
  GetAgroOverviewTabelaVariaveisResponse,
} from 'types/Meteorology/Agro/Overview/api';

const RISK_LEVELS: RiskLevel[] = ['baixo', 'moderado', 'alto', 'muito_alto'];

/**
 * Fonte manda `Baixo|Moderado|Alto|Muito alto`; o FE usa lowercase com o
 * 4º nível `muito_alto` (DECISÃO-3). Valor fora do enum vira null (defensivo).
 */
export function parseRiskLevel(raw: string | null): RiskLevel | null {
  if (!raw) return null;
  const normalized = raw.trim().toLowerCase().replace(/\s+/g, '_') as RiskLevel;
  return RISK_LEVELS.includes(normalized) ? normalized : null;
}

export function transformGetAgroOverviewKpisResponse(
  response: GetAgroOverviewKpisResponse,
): AgroOverviewSummary {
  const raw = response.data;
  return {
    excedenteHidricoPct: raw.pct_excedente_hidrico,
    estresseHidricoPct: raw.pct_estresse_hidrico,
    riscoEstresseHidrico: parseRiskLevel(raw.risco_estresse_hidrico),
    maiorAcumuladoChuva: {
      mm: raw.maior_acumulado_chuva_7d.valor_mm,
      localidade: raw.maior_acumulado_chuva_7d.localidade,
    },
    periodoReferencia: raw.periodo_referencia,
  };
}

export function transformGetAgroOverviewMapaResponse(
  response: GetAgroOverviewMapaResponse,
): AgroOverviewMapData {
  const raw = response.data;
  return {
    variavel: raw.variavel,
    dataInicio: raw.data_inicio,
    dataFim: raw.data_fim,
    // Tripletas [lat, lon, valor] — lat vem PRIMEIRO (contrato §2, rota 2).
    pontos: raw.dados.map(([latitude, longitude, valor]) => ({
      latitude,
      longitude,
      valor,
    })),
  };
}

export function transformGetAgroOverviewTabelaResponse(
  response: GetAgroOverviewTabelaResponse,
): AgroOverviewLocationsData {
  const raw = response.data;
  return {
    total: raw.total,
    localidades: raw.localidades.map((loc): AgroOverviewLocation => ({
      id: loc.id,
      nome: loc.nome,
      estado: loc.uf,
      latitude: loc.latitude,
      longitude: loc.longitude,
      diasSemChuva: loc.dias_sem_chuva,
      armPct: loc.arm,
      chuva7dMm: loc.chuva_7d,
      risco7d: parseRiskLevel(loc.risco_estresse_hidrico_7d),
      perdaAgua7dMm: loc.perda_agua_7d,
    })),
  };
}

export function transformGetAgroOverviewTabelaVariaveisResponse(
  response: GetAgroOverviewTabelaVariaveisResponse,
): AgroOverviewTableVariable[] {
  return response.data.map((variavel) => ({
    nome: variavel.nome,
    label: variavel.label,
    unidade: variavel.unidade,
    descricao: variavel.descricao,
    ordem: variavel.ordem,
    isDefault: variavel.default,
    isAlertaMapa: variavel.is_alerta_mapa,
  }));
}

export function transformGetAgroOverviewMapaVariaveisResponse(
  response: GetAgroOverviewMapaVariaveisResponse,
): AgroOverviewMapVariable[] {
  return response.data.map((variavel) => ({
    nome: variavel.nome,
    label: variavel.label,
    unidade: variavel.unidade,
    periodo: variavel.periodo,
    isDefault: variavel.default,
    // ADENDO-4: passthrough da config oficial; BE antigo (sem o campo) -> null.
    config: variavel.config ?? null,
  }));
}

/**
 * Card 4 (DECISÃO-5): sufixo `-{UF}` quando a UF for resolvida por match do
 * nome contra `localidades[]` da tabela; sem match (ou uf null), só o nome.
 * Se o nome já terminar com `-{UF}`, não duplica o sufixo.
 */
export function localidadeComUf(
  localidade: string,
  locations: AgroOverviewLocation[],
): string {
  const match = locations.find((l) => l.nome === localidade);
  if (!match || !match.estado) return localidade;
  const sufixo = `-${match.estado}`;
  return localidade.endsWith(sufixo) ? localidade : `${localidade}${sufixo}`;
}
